// ============================================================================
// FSA OPENING BALANCE RECONCILIATION CARD  (FSA-2.3)
// ============================================================================
// Renders on the General/Basic tab when (a) the engagement has a
// prior_engagement_id linked AND (b) the consolidated TB has been uploaded
// AND (c) one or more ledger discrepancies exist.
//
// Hides automatically when there are no discrepancies (all deltas ≤ 0.01).
// Read-only. Auditor typically resolves by posting a current-year
// correcting JE for the difference, or accepting as a reconciling item.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import fsaEngagementService from 'api/services/fsa/fsaEngagementService';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';
import type { OpeningBalanceDiscrepancy } from 'api/types/fsa/rollForward.types';

interface Props {
  engagementId: ID;
  /** Card refreshes when this counter increments — caller can wire it to TB upload completion. */
  refreshKey?: number;
}

const fmtAmount = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FsaOpeningBalanceReconciliationCard: React.FC<Props> = ({ engagementId, refreshKey }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<OpeningBalanceDiscrepancy[]>([]);
  const [dismissed, setDismissed] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fsaEngagementService.getOpeningBalanceDiscrepancies(engagementId);
      setRows(response.data ?? []);
    } catch {
      // Silently fail — card is non-essential; toast only on user-initiated refresh.
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleRefresh = useCallback(async () => {
    setDismissed(false);
    try {
      const response = await fsaEngagementService.getOpeningBalanceDiscrepancies(engagementId);
      setRows(response.data ?? []);
      notificationService.toast.success(
        `${response.data?.length ?? 0} discrepanc${(response.data?.length ?? 0) === 1 ? 'y' : 'ies'} found.`,
      );
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Could not refresh discrepancies.';
      notificationService.toast.error(msg, 6000);
    }
  }, [engagementId]);

  // Hide card entirely when nothing to show or auditor dismissed.
  if (dismissed || (!loading && rows.length === 0)) return null;

  return (
    <div style={{
      padding: '1rem', borderRadius: 4,
      background: 'rgba(217, 119, 6, 0.06)',
      border: '1px solid rgba(217, 119, 6, 0.25)',
      display: 'flex', flexDirection: 'column', gap: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <IconSystem.alert size={16} />
        <strong style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
          Opening balance reconciliation
        </strong>
        {!loading && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {rows.length} ledger{rows.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
            The following ledgers' uploaded opening balance differs from the prior year's
            audited closing (closing + posted adjustments). Typical resolution: post a
            current-year correcting JE for the difference, or accept as a reconciling item.
          </div>

          <div style={{
            background: 'var(--bg-primary)', borderRadius: 4,
            border: '1px solid var(--border-color)', overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead style={{ background: 'var(--bg-secondary)' }}>
                <tr>
                  <th style={{ textAlign: 'left',  padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>Ledger</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>Uploaded</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>Audited PY</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>Δ</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 10).map((r) => (
                  <tr key={r.entryId} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <div>{r.ledgerName}</div>
                      {r.scheduleClassName && (
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                          {r.scheduleClassName}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtAmount(r.uploadedOpeningAmount)}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtAmount(r.auditedPriorClosing)}
                    </td>
                    <td style={{
                      padding: '0.5rem 0.75rem', textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: r.delta < 0 ? '#dc2626' : '#16a34a',
                      fontWeight: 600,
                    }}>
                      {r.delta > 0 ? '+' : ''}{fmtAmount(r.delta)}
                    </td>
                  </tr>
                ))}
                {rows.length > 10 && (
                  <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td colSpan={4} style={{
                      padding: '0.5rem 0.75rem', textAlign: 'center',
                      fontSize: '0.75rem', color: 'var(--text-secondary)',
                    }}>
                      … and {rows.length - 10} more
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" style={{ fontSize: '0.8125rem' }}
                    onClick={() => setDismissed(true)}>
              Dismiss
            </button>
            <button className="btn btn-secondary" style={{ fontSize: '0.8125rem' }}
                    onClick={handleRefresh}>
              <IconSystem.refresh size={14} />
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FsaOpeningBalanceReconciliationCard;
